import { useCallback, useEffect, useState } from 'react';

import buyerModule from '../../../api/modules/buyer.module';
import useDebounce from '../../../hooks/useDebounce';
import { getOrderStatusOptions } from '../../../utils/orderStatus';
import OrderDetailsDrawer from '../../SellerDashboard/OrderTable/OrderDetailsDrawer';
import { OrderHistoryTableView } from './OrderHistoryTable.view';

/**
 * The buyer's order history, the container.
 *
 * The request is unchanged: same `buildParams`, same `getBuyerOrders` call with
 * `page`, `limit: 10`, `search` and `status`, same 500ms debounce, same reading
 * of `response.orders`, `response.page`, `response.page_size` and
 * `response.total_pages`.
 *
 * The drawer is the same shared `OrderDetailsDrawer` with `seller={false}`,
 * which is what puts the review form in it.
 *
 * The file kept its default export name from before, when this component was
 * confusingly called `SavedTable` in a directory called `OrderHistoryTable`.
 * That name is corrected here; nothing imports it by name, only by path.
 */
export default function OrderHistoryTable() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total_pages: 0 });

  const debouncedSearch = useDebounce(searchTerm, 500);

  const buildParams = (params) =>
    Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) =>
          value !== '' &&
          value !== null &&
          value !== undefined &&
          !(typeof value === 'number' && isNaN(value)),
      ),
    );

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = buildParams({
        page: currentPage,
        limit: 10,
        search: debouncedSearch?.trim(),
        status: statusFilter || '',
      });

      const { success, data: response, error } = await buyerModule.getBuyerOrders(params);

      if (success) {
        setData(response?.orders || []);
        setPagination({
          page: response?.page || 1,
          pageSize: response?.page_size || 10,
          total_pages: response?.total_pages || 0,
        });
      } else {
        console.error('Error fetching orders:', error);
        setData([]);
        setPagination({ page: 1, pageSize: 10, total_pages: 0 });
        setError(error || 'Could not load your orders.');
      }
    } catch (err) {
      console.error('Unexpected error fetching orders:', err);
      setData([]);
      setPagination({ page: 1, pageSize: 10, total_pages: 0 });
      setError('Could not load your orders.');
    }

    setLoading(false);
  }, [currentPage, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return (
    <OrderHistoryTableView
      rows={data}
      loading={loading}
      error={error}
      onRetry={fetchOrders}
      search={searchTerm}
      onSearchChange={setSearchTerm}
      status={statusFilter}
      onStatusChange={setStatusFilter}
      statusOptions={getOrderStatusOptions()}
      page={pagination.page}
      pageCount={pagination.total_pages}
      pageSize={pagination.pageSize}
      total={data.length}
      onPageChange={setCurrentPage}
      onView={(order) => {
        setSelectedOrder(order);
        setOpenDrawer(true);
      }}
      drawer={
        selectedOrder ? (
          <OrderDetailsDrawer
            isOpen={openDrawer}
            onClose={() => setOpenDrawer(false)}
            order={selectedOrder}
            seller={false}
          />
        ) : null
      }
    />
  );
}
